import { Images } from "@assets/assets";

/**
 * Shape intentionally mirrors what the API will eventually return
 * (see `getPortfolioDetailsByID` / `portfolioModalInterface`) so swapping
 * this static array for a fetched array later requires no JSX changes —
 * only mapping the API response into this interface.
 */
export interface CaseStudyImpactPoint {
  id: number;
  text: string;
}

export interface CaseStudyTag {
  id: number;
  name: string;
}

export interface CaseStudyData {
  id: number;
  title: string;
  tags: CaseStudyTag[];
  challenge: string;
  solution: string;
  impact: CaseStudyImpactPoint[];
  image: any; // portfolio/case-study preview image
  caseStudyHref: string; // PDF download/view link for "View Case Study"
}

export interface ClientEntry {
  id: number;
  name: string;
  logo?: any; // optional logo image asset; falls back to text name if absent
  caseStudy: CaseStudyData;
}

/* ------------------------------------------------------------------ */
/* Static data — the 6 live projects.                                  */
/* TODO: replace placeholder challenge/solution/impact/tags/image      */
/* copy per project with the real content once available; only the    */
/* `caseStudyHref` PDF links are final.                                */
/* ------------------------------------------------------------------ */

export const CLIENT_ENTRIES: ClientEntry[] = [
  {
    id: 1,
    name: "AllProColor",
    logo: Images?.brand1CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 1,
      title: "Modernizing Print Ordering Through Smarter Digital Experiences",
      tags: [
        { id: 1, name: "Artificial Intelligence" },
        { id: 2, name: "Print Commerce" },
        { id: 3, name: "Design Automation" },
        { id: 4, name: "Web Platform" },
      ],
      challenge:
        "Customers faced a complex print-ordering journey spanning product selection, specifications, customization, artwork, design creation, pricing, and checkout.",
      solution:
        "Konstant unified print configuration, AI-powered design creation, product recommendations, design review, pricing, fulfillment, and checkout into one intelligent experience.",
      impact: [
        { id: 1, text: "82% Faster Print Configuration" },
        { id: 2, text: "76% Smarter AI Design Creation" },
        { id: 3, text: "71% Smoother Order Processing" },
        { id: 4, text: "68% Better Customer Experience" },
      ],
      image: Images?.allProColorCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1788178893_success-story-2-konstantinfo.pdf",
    },
  },
  {
    id: 2,
    name: "MaidMatcherAI",
    logo: Images?.brand2CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 2,
      title: "Simplifying Household Staff Hiring Through Smarter Matchmaking",
      tags: [
        { id: 1, name: "Artificial Intelligence" },
        { id: 2, name: "Recruitment" },
        { id: 3, name: "Marketplace" },
        { id: 4, name: "Mobile App" },
      ],
      challenge:
        "Traditional household staff hiring involved manual searches, limited candidate access, and fragmented communication, making the recruitment process slow and difficult to manage.",
      solution:
        "Konstant developed an AI-powered matchmaking platform with intelligent recommendations, advanced search, secure messaging, and interview management to simplify staff hiring.",
      impact: [
        { id: 1, text: "60% Faster Staff Matching" },
        { id: 2, text: "50% Improved Recruitment Productivity" },
        { id: 3, text: "45% Higher Employer Satisfaction" },
        { id: 4, text: "75% Better Hiring Experience" },
      ],
      image: Images?.maidMatcherCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1778159650_success-story-13-konstantinfo.pdf",
    },
  },
  {
    id: 3,
    name: "Obsessvr",
    logo: Images?.brand3CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 3,
      title: "Creating Immersive 3D Commerce Experiences for Shoppers",
      tags: [
        { id: 1, name: "AR/VR" },
        { id: 2, name: "3D Commerce" },
        { id: 3, name: "Virtual Stores" },
        { id: 4, name: "Web Platform" },
      ],
      challenge:
        "Obsess needed to overcome technology integration and scaling challenges while replacing conventional e-commerce grids with dynamic, interactive shopping experiences.",
      solution:
        "Konstant developed a scalable React.js web application with immersive AR/VR capabilities, enabling brands to deliver interactive shopping without app downloads or headsets.",
      impact: [
        { id: 1, text: "109% Increase in Average Session Time" },
        { id: 2, text: "112% Increase in Customer Checkouts" },
        { id: 3, text: "35% Increase in Average Order Value" },
        { id: 4, text: "$13.4M Funding Raised" },
      ],
      image: Images?.obsessVrCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1778159923_success-story-3-konstantinfo.pdf",
    },
  },
  {
    id: 4,
    name: "MatchMindAI",
    logo: Images?.brand4CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 4,
      title: "Accelerating Talent Discovery With Smarter Recruitment Workflows",
      tags: [
        { id: 1, name: "Artificial Intelligence" },
        { id: 2, name: "Candidate Matching" },
        { id: 3, name: "Automation" },
        { id: 4, name: "Web Platform" },
      ],
      challenge:
        "Traditional recruitment made candidate discovery, screening, and interview coordination time-consuming, while keyword-based searches often failed to identify relevant talent accurately.",
      solution:
        "Konstant developed an AI-powered recruitment platform combining intelligent candidate matching, conversational AI, automated workflows, and streamlined interview scheduling.",
      impact: [
        { id: 1, text: "65% Faster Candidate Shortlisting" },
        { id: 2, text: "55% Improved Hiring Efficiency" },
        { id: 3, text: "50% Reduced Manual Screening" },
        { id: 4, text: "70% Better Candidate Matching Accuracy" },
      ],
      image: Images?.matchMindCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1788243956_success-story-27-konstantinfo.pdf",
    },
  },
  {
    id: 5,
    name: "ChatRedact",
    logo: Images?.brand5CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 5,
      title: "Protecting Sensitive Data With Real-Time AI Redaction",
      tags: [
        { id: 1, name: "Artificial Intelligence" },
        { id: 2, name: "Data Privacy" },
        { id: 3, name: "Compliance" },
        { id: 4, name: "SaaS" },
      ],
      challenge:
        "Enterprises needed to protect sensitive information in real-time conversations while maintaining seamless interactions, regulatory compliance, low latency, and scalable performance.",
      solution:
        "Konstant developed an AI-powered chat platform combining PII detection, customizable redaction filters, dynamic LLM switching, RAG, and secure data handling.",
      impact: [
        { id: 1, text: "82% Faster Data Redaction" },
        { id: 2, text: "78% Improved Privacy Protection" },
        { id: 3, text: "74% Smoother Chat Interactions" },
        { id: 4, text: "80% Better Compliance Readiness" },
      ],
      image: Images?.chatRedactCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1778159338_success-story-5-konstantinfo.pdf",
    },
  },
  {
    id: 6,
    name: "Festivity",
    logo: Images?.brand6CaseStudy, // TODO: replace with real client logo asset
    caseStudy: {
      id: 6,
      title: "Connecting Event Services Through One Seamless Marketplace",
      tags: [
        { id: 1, name: "Event Management" },
        { id: 2, name: "Booking Platform" },
        { id: 3, name: "Mobile App" },
        { id: 4, name: "Web Platform" },
      ],
      challenge:
        "Festivity needed to consolidate diverse event services while supporting complex waiting orders for unavailable vendors and meeting precise client requirements.",
      solution:
        "Konstant built an intuitive multi-vendor web platform and native iOS solution with streamlined vendor registration, booking management, and event-service discovery.",
      impact: [
        { id: 1, text: "75% Faster Vendor Onboarding" },
        { id: 2, text: "68% Smoother Booking Management" },
        { id: 3, text: "72% Faster Service Discovery" },
        { id: 4, text: "80% Better Customer Experience" },
      ],
      image: Images?.festivityCaseStudy, // TODO: replace with real preview image
      caseStudyHref:
        "https://s3-us-west-2.amazonaws.com/konstant-pipeline/k_portfolio/pdfs/1778160003_success-story-8-konstantinfo.pdf",
    },
  },
];
